import type { Card, CardType, MetricDef, MetricKind, MetricModule } from '@/api/boards';
import { ROUTES, cippDevicesView, insightsCoverageView, insightsGapsView } from '@/lib/routes';

/** Custom dashboards: formatting, thresholds, drill-in links and card defaults. */

export const MODULE_LABEL: Record<MetricModule, string> = {
  psa: 'PSA · Autotask', rmm: 'RMM · Datto', cipp: 'Microsoft 365 · CIPP', backup: 'Backup · Datto SaaS', network: 'Network · Sophos / UniFi',
  domains: 'Domains · dmarcian', docs: 'Docs · IT Glue', insights: 'Insights', csp: 'CSP · Microsoft NCE', soc: 'SOC · Huntress',
};

/** Which card types can show which metric kinds (first = default). */
export const CARD_TYPES_FOR: Record<MetricKind, CardType[]> = {
  number: ['kpi'],
  breakdown: ['bar', 'donut'],
  series: ['line'],
  table: ['list'],
};

export const CARD_LABEL: Record<CardType, string> = {
  kpi: 'Number', bar: 'Bar chart', donut: 'Donut', line: 'Trend', list: 'List', note: 'Note', links: 'Links', clock: 'Clock',
};

/** Default size (grid units; 12 columns) per card type. */
export const CARD_SIZE: Record<CardType, { w: number; h: number }> = {
  kpi: { w: 3, h: 2 }, bar: { w: 4, h: 4 }, donut: { w: 4, h: 4 }, line: { w: 6, h: 4 }, list: { w: 4, h: 4 }, note: { w: 4, h: 3 }, links: { w: 3, h: 3 }, clock: { w: 3, h: 2 },
};

export const ROW_HEIGHT = 72;
export const GRID_GAP = 12;

const compact = new Intl.NumberFormat('en-AU', { notation: 'compact', maximumFractionDigits: 1 });
const whole = new Intl.NumberFormat('en-AU', { maximumFractionDigits: 1 });

export function fmtMetric(n: number | null | undefined, unit?: MetricDef['unit']): string {
  if (n == null || !Number.isFinite(n)) return '—';
  if (unit === 'percent') return `${whole.format(n)}%`;
  if (unit === 'currency') return `$${Math.abs(n) >= 10_000 ? compact.format(n) : whole.format(Math.round(n))}`;
  return Math.abs(n) >= 100_000 ? compact.format(n) : whole.format(n);
}

/** ISO dates in table values / axes read as dates. */
export function fmtCell(v: number | string | null, unit?: MetricDef['unit']): string {
  if (v == null) return '—';
  if (typeof v === 'number') return fmtMetric(v, unit);
  if (/^\d{4}-\d{2}-\d{2}/.test(v)) return new Date(v.length === 10 ? `${v}T00:00:00` : v).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  return v;
}

export const fmtAxisDay = (v: string) => (/^\d{4}-\d{2}-\d{2}/.test(v) ? new Date(v.length === 10 ? `${v}T00:00:00` : v).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : v);

export type Level = 'ok' | 'warn' | 'bad' | null;

/**
 * Threshold colouring. "Lower is better" metrics go amber at ≥ warn and red at ≥ bad; "higher is
 * better" ones at ≤ warn / ≤ bad. No thresholds (or a neutral metric) = no colour.
 */
export function levelFor(value: number | null | undefined, card: Card, def: MetricDef | undefined): Level {
  const t = card.options?.thresholds;
  if (value == null || !def?.better || !t || (t.warn == null && t.bad == null)) return null;
  if (def.better === 'down') {
    if (t.bad != null && value >= t.bad) return 'bad';
    if (t.warn != null && value >= t.warn) return 'warn';
  } else {
    if (t.bad != null && value <= t.bad) return 'bad';
    if (t.warn != null && value <= t.warn) return 'warn';
  }
  return 'ok';
}

/** Where a metric card drills to (the page that explains the number). */
export function drillFor(metric: string | undefined): string | null {
  if (!metric) return null;
  const [mod, name] = metric.split('.');
  switch (mod) {
    case 'psa': return /pipeline|forecast|won/i.test(name) ? ROUTES.psa.dashboards.sales : ROUTES.psa.dashboards.serviceDesk;
    case 'rmm': return /patch/i.test(name) ? ROUTES.rmm.dashboards.patching : /alert/i.test(name) ? ROUTES.rmm.alerts : ROUTES.rmm.dashboards.overview;
    case 'cipp': return name === 'intuneNonCompliant' ? cippDevicesView({ compliance: 'noncompliant' }) : name === 'intuneNotInRmm' ? cippDevicesView({ rmm: 'missing' }) : ROUTES.cipp.dashboards.security;
    case 'backup': return ROUTES.backup.saas;
    case 'sophos': return ROUTES.network.sophos.dashboard;
    case 'unifi': return name === 'wan' ? ROUTES.network.unifi.wan : ROUTES.network.unifi.dashboard;
    case 'domains': return ROUTES.domains.root;
    case 'docs': return ROUTES.docs.expirations;
    case 'insights': return name === 'coverage' || name === 'worstCoverage' ? insightsCoverageView({ tab: 'companies' }) : name === 'drift' ? insightsCoverageView({ state: 'drift' }) : name === 'criticalGaps' ? insightsGapsView({ severity: 'critical' }) : ROUTES.insights.gaps;
    case 'csp': return ROUTES.csp.dashboard;
    case 'soc': return name === 'noEdr' ? insightsGapsView({ kind: 'no-edr' }) : /incident|severity|weekly|recent|critical/i.test(name) ? ROUTES.soc.incidents : name === 'identitiesNoMfa' ? ROUTES.soc.identities : ROUTES.soc.agents;
    default: return null;
  }
}

export const newCardId = () => `c${Math.random().toString(36).slice(2, 10)}`;

/** Next free row for a new card (bottom of the grid). */
export const nextY = (cards: Card[]) => cards.reduce((m, c) => Math.max(m, c.y + c.h), 0);
